import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {UseFormSetValue} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {useGetFixedIndicatorsQuery} from '@app/store/api/Nemesis';

import {
  IndicateurMethodeValues,
  IndicatorDimensionsValues,
  IndicatorDonneeValues,
  IndicatorPossibleFilters,
  IndicatorRestitutionValues,
  IndicatorTissotParameter,
} from '../indicator-types';
import {toIndicatorTissotFilters} from '../utils/filter-initializer';
import {AutocompleteSelectAll} from './_partial/autocomplete-select-all';
import {IndicatorFormFilter} from './form/indicator-form-filter';
import {CreateIndicatorSchemaType} from './indicator-edit-form';

type Props = {
  params: IndicatorTissotParameter;
  setValue: UseFormSetValue<CreateIndicatorSchemaType>;
  fixedIndicatorId: number;
};

export const IndicatorParameterForm = ({params, setValue, fixedIndicatorId}: Props) => {
  const {t} = useTranslation();
  const {data: fixedIndicators} = useGetFixedIndicatorsQuery();

  const handleDonneeChange = (value: string) => {
    if (value === '') {
      setField('methode', 'sum');
    }
    setField('donnee', value);
  };

  const setField = (field: string, value: any) => {
    setValue(`config.${field}` as keyof CreateIndicatorSchemaType, value);
  };

  const showFullOptions = fixedIndicatorId === 0;

  return (
    <>
      <Card>
        <Stack divider={<Divider />} spacing={2}>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h6" gutterBottom>
                {t('pages.pilotage-rh.indicators.form.data_and_presentation')}
              </Typography>

              <InputLabel id="parameter-y-label">{t('pages.pilotage-rh.indicators.form.configuration')}</InputLabel>
              <Select
                labelId="parameter-fixed-label"
                id="parameter-fixed"
                value={fixedIndicatorId}
                label="Fixé"
                onChange={(event) => setValue('fixedIndicatorId', Number(event.target.value))}
              >
                <MenuItem key={0} value={0}>
                  {t('pages.pilotage-rh.indicators.form.custom')}
                </MenuItem>
                {fixedIndicators?.map((i) => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.className} <span style={{color: '#777777', marginLeft: '2rem'}}>{i.description}</span>
                  </MenuItem>
                ))}
              </Select>

              {showFullOptions && (
                <>
                  <InputLabel id="parameter-y-label">{t('pages.pilotage-rh.indicators.form.row')}</InputLabel>
                  <Select
                    labelId="parameter-y-label"
                    id="parameter-y"
                    value={params.y}
                    label="Ligne"
                    onChange={(event) => setField('y', event.target.value)}
                  >
                    {IndicatorDimensionsValues.map((dimension) => (
                      <MenuItem key={dimension.value} value={dimension.value}>
                        {dimension.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                    <Typography variant="subtitle2" sx={{mr: 1, color: 'text.secondary'}}>
                      {t('pages.pilotage-rh.indicators.form.add_total_row')}
                    </Typography>
                    <Checkbox checked={params.totalY} onChange={(event) => setField('totalY', event.target.checked)} />
                  </Box>
                </>
              )}
              <InputLabel id="parameter-x-label">{t('pages.pilotage-rh.indicators.form.column')}</InputLabel>
              <Select
                labelId="parameter-x-label"
                id="parameter-x"
                value={params.x}
                label="Colonne"
                onChange={(event) => setField('x', event.target.value)}
              >
                {IndicatorDimensionsValues.map((dimension) => (
                  <MenuItem key={dimension.value} value={dimension.value}>
                    {dimension.label}
                  </MenuItem>
                ))}
              </Select>

              {showFullOptions && (
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1, color: 'text.secondary'}}>
                  <Typography variant="subtitle2" sx={{mr: 1}}>
                    {t('pages.pilotage-rh.indicators.form.add_total_column')}
                  </Typography>
                  <Checkbox checked={params.totalX} onChange={(event) => setField('totalX', event.target.checked)} />
                </Box>
              )}
              <InputLabel>{t('pages.pilotage-rh.indicators.form.selectable_columns')}</InputLabel>
              <AutocompleteSelectAll
                onChange={(options) =>
                  setField(
                    'possibleX',
                    options.map((option) => (typeof option.id === 'string' ? option.id : option.id.toString())),
                  )
                }
                options={IndicatorDimensionsValues.map((dimension) => ({id: dimension.value, label: dimension.label}))}
                value={(params.possibleX ?? []).map((value) => ({
                  id: value,
                  label: IndicatorDimensionsValues.find((d) => d.value === value)?.label || '',
                }))}
              />

              {showFullOptions && (
                <>
                  <InputLabel id="parameter-donnee-label">{t('pages.pilotage-rh.indicators.form.data')}</InputLabel>
                  <Select
                    labelId="parameter-donnee-label"
                    id="parameter-donnee"
                    value={params.donnee}
                    label="donnee"
                    onChange={(event) => handleDonneeChange(event.target.value)}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected === '') {
                        const emptyOption = IndicatorDonneeValues.find((d) => d.value === '');
                        return emptyOption ? emptyOption.label : '';
                      }
                      const selectedOption = IndicatorDonneeValues.find((d) => d.value === selected);
                      return selectedOption ? selectedOption.label : selected;
                    }}
                  >
                    {IndicatorDonneeValues.map((dimension, index) => (
                      <MenuItem key={index} value={dimension.value}>
                        {dimension.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {params.donnee !== '' && (
                    <>
                      <InputLabel id="parameter-calcul-label">
                        {t('pages.pilotage-rh.indicators.form.calculation_method')}
                      </InputLabel>
                      <Select
                        labelId="parameter-calcul-label"
                        id="parameter-calcul"
                        value={params.methode}
                        label="Méthode de calcul"
                        onChange={(event) => setField('methode', event.target.value)}
                        displayEmpty
                      >
                        {IndicateurMethodeValues.map((dimension, index) => (
                          <MenuItem key={index} value={dimension.value}>
                            {dimension.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                  <InputLabel>{t('pages.pilotage-rh.indicators.form.selectable_data')}</InputLabel>
                  <AutocompleteSelectAll
                    onChange={(options) =>
                      setField(
                        'possibleDonnees',
                        options.map((option) => (typeof option.id === 'string' ? option.id : option.id.toString())),
                      )
                    }
                    options={IndicatorDonneeValues.map((dimension) => ({id: dimension.value, label: dimension.label}))}
                    value={(params.possibleDonnees ?? []).map((value) => ({
                      id: value,
                      label: IndicatorDonneeValues.find((d) => d.value === value)?.label || '',
                    }))}
                  />
                </>
              )}
              <InputLabel id="parameter-donnee-chart">{t('pages.pilotage-rh.indicators.form.presentation')}</InputLabel>
              <Select
                labelId="parameter-donnee-chart"
                id="parameter-chart"
                value={params.chartType}
                label="chartType"
                onChange={(event) => setField('chartType', event.target.value)}
              >
                {IndicatorRestitutionValues.map((dimension) => (
                  <MenuItem key={dimension.value} value={dimension.value}>
                    {dimension.label}
                  </MenuItem>
                ))}
              </Select>
              <InputLabel>{t('pages.pilotage-rh.indicators.form.possible_presentations')}</InputLabel>
              <AutocompleteSelectAll
                onChange={(options) =>
                  setField(
                    'possibleChartTypes',
                    options.map((option) => (typeof option.id === 'string' ? option.id : option.id.toString())),
                  )
                }
                options={IndicatorRestitutionValues.map((dimension) => ({id: dimension.value, label: dimension.label}))}
                value={(params.possibleChartTypes ?? []).map((value) => ({
                  id: value,
                  label: IndicatorRestitutionValues.find((d) => d.value === value)?.label || '',
                }))}
              />
            </Stack>
          </CardContent>
        </Stack>
      </Card>

      {showFullOptions && (
        <Card>
          <Stack divider={<Divider />} spacing={2}>
            <CardContent>
              <Stack gap={2}>
                <Typography variant="h6" gutterBottom>
                  {t('pages.pilotage-rh.indicators.form.filters')}
                </Typography>
                <IndicatorFormFilter setField={setField} filters={params.filters} />
              </Stack>
            </CardContent>
          </Stack>
        </Card>
      )}
      <Card>
        <Stack divider={<Divider />} spacing={2}>
          <CardContent>
            <Stack gap={2}>
              <Typography variant="h6" gutterBottom>
                {t('pages.pilotage-rh.indicators.form.available_filters_for_user')}
              </Typography>
              <AutocompleteSelectAll
                onChange={(options) => {
                  const possibleFilters = options.map((option) =>
                    typeof option.id === 'string' ? option.id : option.id.toString(),
                  );
                  const filters = params.filters || {};
                  setField('filters', toIndicatorTissotFilters(filters));
                  setField('possibleFilters', possibleFilters);
                }}
                options={IndicatorPossibleFilters.map((filter) => ({id: filter.value, label: filter.label}))}
                value={(params.possibleFilters ?? []).map((value) => ({
                  id: value,
                  label: IndicatorPossibleFilters.find((d) => d.value === value)?.label || '',
                }))}
              />
            </Stack>
          </CardContent>
        </Stack>
      </Card>
    </>
  );
};
